import React, { useState } from "react"

import useCreateUserLicense from "~/api/hooks/useCreateUserLicense"

// Map state codes to full names
const STATE_NAMES: Record<string, string> = {
  AL: `Alabama`,
  AK: `Alaska`,
  AZ: `Arizona`,
  AR: `Arkansas`,
  CA: `California`,
  CO: `Colorado`,
  CT: `Connecticut`,
  DE: `Delaware`,
  FL: `Florida`,
  GA: `Georgia`,
  HI: `Hawaii`,
  ID: `Idaho`,
  IL: `Illinois`,
  IN: `Indiana`,
  IA: `Iowa`,
  KS: `Kansas`,
  KY: `Kentucky`,
  LA: `Louisiana`,
  ME: `Maine`,
  MD: `Maryland`,
  MA: `Massachusetts`,
  MI: `Michigan`,
  MN: `Minnesota`,
  MS: `Mississippi`,
  MO: `Missouri`,
  MT: `Montana`,
  NE: `Nebraska`,
  NV: `Nevada`,
  NH: `New Hampshire`,
  NJ: `New Jersey`,
  NM: `New Mexico`,
  NY: `New York`,
  NC: `North Carolina`,
  ND: `North Dakota`,
  OH: `Ohio`,
  OK: `Oklahoma`,
  OR: `Oregon`,
  PA: `Pennsylvania`,
  RI: `Rhode Island`,
  SC: `South Carolina`,
  SD: `South Dakota`,
  TN: `Tennessee`,
  TX: `Texas`,
  UT: `Utah`,
  VT: `Vermont`,
  VA: `Virginia`,
  WA: `Washington`,
  WV: `West Virginia`,
  WI: `Wisconsin`,
  WY: `Wyoming`,
  DC: `District of Columbia`,
}

interface StateLicenseFormProps {
  state: string
  userId: string
  onSuccess: () => void
}

export const StateLicenseForm: React.FC<StateLicenseFormProps> = ({
  state,
  userId,
  onSuccess,
}) => {
  const [licenseNumber, setLicenseNumber] = useState(``)
  const [expirationDate, setExpirationDate] = useState(``)

  const { mutate: createLicense, isLoading, error } = useCreateUserLicense()

  const stateName = STATE_NAMES[state.toUpperCase()] || state

  const handleSubmit = () => {
    if (!licenseNumber.trim() || !expirationDate) {
      return
    }

    createLicense(
      {
        userId,
        dto: {
          state: state.toUpperCase(),
          license: licenseNumber.trim(),
          expirationDate,
        },
      },
      {
        onSuccess: () => {
          onSuccess()
        },
      },
    )
  }

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-6">
      <h3 className="text-lg font-semibold text-blue-700">
        State Licensing Information Required
      </h3>
      <p className="mt-2 text-blue-600">
        Please provide your state license information for {stateName}
      </p>

      <div className="mt-4 space-y-4">
        <div>
          <label
            htmlFor="licenseNumber"
            className="block text-sm font-medium text-gray-700"
          >
            State License Number ({stateName})
          </label>
          <input
            type="text"
            id="licenseNumber"
            value={licenseNumber}
            onChange={(e) => setLicenseNumber(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Enter license number"
          />
        </div>

        <div>
          <label
            htmlFor="expirationDate"
            className="block text-sm font-medium text-gray-700"
          >
            License Expiration Date
          </label>
          <input
            type="date"
            id="expirationDate"
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600">
            {error.message || `Failed to save license. Please try again.`}
          </p>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading || !licenseNumber.trim() || !expirationDate}
          className="w-full rounded-md bg-blue-600 px-4 py-3 font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? `Saving...` : `Save & Resubmit`}
        </button>
      </div>
    </div>
  )
}
